import { Link } from "react-router-dom";
import { SITE, GUARANTEES, SERVICES, STATS } from "../data/site";
import { Reveal, SectionHead } from "../components/Reveal";
import { PageHero, CtaBand } from "../components/Shared";
import { Seo, localBusinessLD } from "../lib/seo";

export default function AProposPage() {
    return (
        <>
            <Seo
                title="À propos — Solino Pro, artisan peintre & sols à Chaumont-en-Vexin"
                description="Solino Pro, artisan BTP à Chaumont-en-Vexin : rénovation intérieure, sols et murs — peinture, parquet, carrelage depuis plus de 10 ans. Découvrez notre histoire, nos engagements et nos garanties."
                path="/a-propos"
                jsonld={localBusinessLD()}
            />
            <PageHero
                img="https://images.unsplash.com/photo-1624574470112-46944be68409?q=80&w=1800&auto=format&fit=crop"
                crumb="Solino Pro — À propos"
                title="Un artisan BTP,"
                serif="pas une chaîne."
            />

            <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center" data-testid="apropos-story">
                <Reveal>
                    <div className="relative overflow-hidden border border-linew">
                        <img
                            src="https://images.unsplash.com/photo-1673978483693-9e4be55b2a35?q=80&w=1400&auto=format&fit=crop"
                            alt="Solino Pro au travail sur un chantier de parquet"
                            loading="lazy"
                            className="w-full aspect-[4/5] object-cover"
                        />
                        <span className="absolute bottom-5 left-5 glass border border-linew px-4 py-2 text-xs uppercase tracking-[0.2em] text-gold">
                            Chaumont-en-Vexin ({SITE.dept})
                        </span>
                    </div>
                </Reveal>
                <div>
                    <SectionHead
                        eyebrow="Notre histoire"
                        title="Installé à Chaumont-en-Vexin,"
                        serif="attaché au Vexin."
                    />
                    <div className="mt-8 space-y-5 text-smoke leading-relaxed">
                        <Reveal delay={0.1}>
                            <p>
                                Solino Pro, c'est l'affaire d'un artisan BTP installé à
                                Chaumont-en-Vexin, au cœur du Vexin français, depuis plus de dix
                                ans. Pose de sols, finitions murales, habillage d'escaliers,
                                ajustement de portes, peinture et carrelage : un seul interlocuteur
                                pour tout ce qui touche à vos murs et à vos sols.
                            </p>
                        </Reveal>
                        <Reveal delay={0.16}>
                            <p>
                                Plus de 500 projets réalisés dans le secteur, du remplacement d'une
                                porte à la rénovation complète d'une maison : notre conviction est
                                simple. Là où les plateformes enchaînent les sous-traitants, la
                                proximité fait la différence. Vous nous appelez, c'est nous qui
                                venons, c'est nous qui travaillons chez vous — et c'est nous qui
                                restons joignables après le chantier.
                            </p>
                        </Reveal>
                        <Reveal delay={0.22}>
                            <p>
                                Chaque projet démarre par un devis détaillé gratuit sous 48 h, poste
                                par poste, pour que vous sachiez exactement ce que vous payez.
                            </p>
                        </Reveal>
                    </div>
                    <Reveal delay={0.26}>
                        <dl className="mt-10 grid grid-cols-3 gap-4 border-t border-linew pt-8" data-testid="apropos-stats">
                            {STATS.map((s) => (
                                <div key={s.label}>
                                    <dt className="font-display italic text-4xl text-gold">{s.value}</dt>
                                    <dd className="mt-2 text-xs uppercase tracking-[0.15em] text-smoke leading-snug">{s.label}</dd>
                                </div>
                            ))}
                        </dl>
                    </Reveal>
                </div>
            </section>

            <section className="border-y border-linew bg-coal/60" data-testid="apropos-values">
                <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 lg:py-28">
                    <SectionHead eyebrow="Nos engagements" title="Trois promesses" serif="tenues." />
                    <div className="mt-12 grid md:grid-cols-3 gap-4">
                        {GUARANTEES.map((g, i) => (
                            <Reveal key={g} delay={i * 0.08}>
                                <div className="h-full border border-linew bg-ink p-8">
                                    <span className="font-display italic text-5xl text-gold/25" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                                    <h3 className="mt-4 text-lg text-ivory font-medium">{g}</h3>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.2}>
                        <p className="mt-8 text-xs text-smoke">
                            {SITE.siret} · {SITE.decennale}. Retrouvez-nous sur Instagram :{" "}
                            <a href={SITE.instagram} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline" data-testid="apropos-instagram">
                                @solinopro
                            </a>.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 lg:py-28" data-testid="apropos-services">
                <SectionHead eyebrow="Nos métiers" title="Tout ce qui touche" serif="à vos murs et sols." />
                <div className="mt-12 flex flex-wrap gap-3">
                    {SERVICES.map((s) => (
                        <Link
                            key={s.slug}
                            to={`/${s.slug}`}
                            className="rounded-full border border-linew px-6 py-3 text-sm text-ivory/80 hover:border-gold hover:text-gold transition-colors"
                            data-testid={`apropos-service-${s.slug}`}
                        >
                            {s.nav}
                        </Link>
                    ))}
                </div>
            </section>

            <CtaBand />
        </>
    );
}
